import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'
import CustomerAddressFields from './CustomerAddressFields'
import ExistingCompaniesNotice from './ExistingCompaniesNotice'
import { findExistingCompanies, type ExistingCompany } from '../lib/existingCompanies'
import { emptyAddressDraft, isAddressDraftBlank, validateAddressDraft, addressDraftToRow, type AddressDraft } from '../lib/customerAddress'

interface CreateCustomerDialogProps {
  isOpen: boolean
  initialName: string
  fieldType?: 'importer' | 'consignor' | 'notify' | 'agent' | 'other'
  onClose: () => void
  // `address` = the first address that was saved with it (for the caller's preview); undefined when none was entered.
  onSuccess: (customer: { id: string; code: string; name: string }, address?: { address_line_1: string; address_line_2: string; city: string; postcode: string; country: string }) => void
}


const COMPANY_TYPES = [
  { value: 'IMPORTER', label: 'Importer / Consignee' },
  { value: 'EXPORTER', label: 'Consignor / Shipper / Exporter' },
  { value: 'NOTIFY', label: 'Notify Party' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'BROKER', label: 'Broker' },
  { value: 'OTHER', label: 'Other' },
]

const generateCode = (name: string): string => {
  const initials = name
    .split(' ')
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join('')
  const randomNum = Math.floor(1000 + Math.random() * 9000)
  return `${initials}${randomNum}`
}

const getDefaultCompanyTypes = (fieldType?: string): string[] => {
  switch (fieldType) {
    case 'importer':
      return ['IMPORTER']
    case 'consignor':
      return ['EXPORTER']
    case 'notify':
      return ['NOTIFY']
    case 'agent':
      return ['AGENT']
    default:
      return []
  }
}

export default function CreateCustomerDialog({
  isOpen,
  initialName,
  fieldType,
  onClose,
  onSuccess,
}: CreateCustomerDialogProps) {
  const [form, setForm] = useState({
    code: '',
    name: initialName,
    company_types: getDefaultCompanyTypes(fieldType),
    contact_email: '',
    contact_phone: '',
    is_active: true,
  })
  // First address -> customer_addresses (NOT the legacy customers columns this window used to write).
  const [addr, setAddr] = useState<AddressDraft>(emptyAddressDraft())
  // Set once the company row exists, so a failed address save is retried WITHOUT creating the company a second time.
  const [createdCustomer, setCreatedCustomer] = useState<{ id: string; code: string; name: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Companies the database already has that look like this one; while this is set nothing is created (see ExistingCompaniesNotice).
  const [existing, setExisting] = useState<ExistingCompany[] | null>(null)

  // Update form when dialog opens or when initialName/fieldType changes
  useEffect(() => {
    if (isOpen) { setAddr(emptyAddressDraft()); setCreatedCustomer(null); setExisting(null) }
    if (isOpen && initialName) {
      setForm((prev) => ({
        ...prev,
        name: initialName,
        code: generateCode(initialName),
        company_types: getDefaultCompanyTypes(fieldType),
      }))
    }
  }, [isOpen, initialName, fieldType])

  if (!isOpen) return null

  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const toggleCompanyType = (type: string) => {
    setForm((prev) => ({
      ...prev,
      company_types: prev.company_types.includes(type)
        ? prev.company_types.filter((t) => t !== type)
        : [...prev.company_types, type],
    }))
  }

  const validateForm = (): string | null => {
    if (!form.code.trim()) return 'Code is required'
    if (!form.name.trim()) return 'Name is required'
    return null
  }

  const handleSave = async (skipExistingCheck = false) => {
    const validationError = createdCustomer ? null : validateForm()
    if (validationError) {
      setError(validationError)
      return
    }
    // Validate the address BEFORE creating anything, so a bad address can't leave a half-made company behind.
    const addrFilled = !isAddressDraftBlank(addr)
    if (addrFilled) {
      const addrErr = validateAddressDraft(addr)
      if (addrErr) { setError(addrErr); return }
    }

    setSaving(true)
    setError(null)

    try {
      let customer = createdCustomer
      if (!customer && !skipExistingCheck) {
        // Fresh query against the database, not the list the dropdown loaded earlier.
        const found = await findExistingCompanies(form.name, form.code)
        if (found.length > 0) { setExisting(found); setSaving(false); return }
      }
      if (!customer) {
        const insertData = {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          company_types: form.company_types,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          // Company country follows the address country typed here (default GB), as before. The address itself is NOT written to
          // the legacy customers.address_line_*/city/postcode columns any more -- it goes to customer_addresses below.
          country: addr.country || 'GB',
          country_code: addr.country || 'GB',
          branch: 'BIRMINGHAM',
          is_active: form.is_active,
        }
        const { data, error: dbError } = await supabase
          .from('customers')
          .insert([insertData])
          .select('id, code, name')
          .single()
        if (dbError) {
          const errorMsg = (dbError as any).message || (dbError as any).details || 'Unknown error'
          const errorCode = (dbError as any).code || ''
          throw new Error(`Failed to create customer: ${errorMsg}${errorCode ? ` (${errorCode})` : ''}`)
        }
        customer = data
        setCreatedCustomer(data)
      }

      if (addrFilled) {
        const { error: addrErr } = await supabase.from('customer_addresses').insert(addressDraftToRow(addr, customer!.id, true))
        if (addrErr) {
          // The company exists now; stay open and say so. Pressing Save again retries only the address.
          throw new Error(`Company "${customer!.name}" was created, but its address was NOT saved: ${addrErr.message}. Press Save to retry the address.`)
        }
      }

      const lines = addr.address_lines.map(l => l.trim()).filter(Boolean)
      onSuccess(customer!, addrFilled ? { address_line_1: lines[0] ?? '', address_line_2: lines.slice(1).join(', '), city: addr.city, postcode: addr.postcode, country: addr.country } : undefined)
      onClose()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save customer'
      console.error('[CreateCustomerDialog] Save error:', errorMsg, err)
      setError(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-h-screen overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Create New Customer
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-200 max-h-24 overflow-y-auto">
            {error}
          </div>
        )}

        {existing && (
          <ExistingCompaniesNotice
            typedName={form.name.trim()}
            matches={existing}
            busy={saving}
            onUse={c => { onSuccess({ id: c.id, code: c.code, name: c.name }); onClose() }}
            onCreateAnyway={() => { setExisting(null); handleSave(true) }}
          />
        )}

        <div className="space-y-6">
          {/* 🔹 基础信息 */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Basic Information
            </h3>

            {/* Code - Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., SMK1234"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Auto-generated from name, can be edited
              </p>
            </div>

            {/* Name - Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Company name"
              />
            </div>
          </div>

          {/* 🔹 公司类型 */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Company Type
            </h3>
            <div className="space-y-2">
              {COMPANY_TYPES.map((type) => (
                <label key={type.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={form.company_types.includes(type.value)}
                    onChange={() => toggleCompanyType(type.value)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {type.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* 🔹 联系方式 */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Contact Information
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="contact@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+44 1234 567890"
              />
            </div>
          </div>

          {/* Address -> customer_addresses (shared block, same as the company page and New Company) */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Address
            </h3>
            <CustomerAddressFields value={addr} onChange={patch => setAddr(p => ({ ...p, ...patch }))} showRoles />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
